/**
 * PostHog initialization.
 * PostHog collects explicit business events plus controlled presence signals.
 * Raw PostHog system events are intentionally kept out of interaction counts.
 */
import posthog from 'posthog-js';

import { isMemberAnalyticsPath } from './routePolicy';

let initialized = false;
/**
 * Stop heartbeat emission (called on cleanup if needed).
 */
export function stopHeartbeat(): void {
  // Heartbeats are no longer emitted. Keep the API for existing imports.
}

export function initPostHog(): void {
  if (typeof window === 'undefined' || initialized) return;
  if (!isMemberAnalyticsPath(window.location.pathname)) return;

  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  const host =
    process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://app.posthog.com';

  if (!key) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        '[Analytics] NEXT_PUBLIC_POSTHOG_KEY not set — PostHog disabled'
      );
    }
    return;
  }

  try {
    posthog.init(key, {
      api_host: host,

      // The member site has an explicit, audited business-event catalogue.
      // Raw DOM clicks duplicate those events and add substantial billable
      // volume without adding business context.
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,

      persistence: 'localStorage',
      person_profiles: 'identified_only',

      request_batching: true,
      rageclick: false,
      disable_session_recording: true,

      // Shared components can render on both sites. This final transport guard
      // prevents explicit events from leaving admin routes.
      before_send: (event) =>
        isMemberAnalyticsPath(window.location.pathname) ? event : null,

      property_denylist: ['password', 'credit_card'],

      cross_subdomain_cookie: false,
      secure_cookie: window.location.protocol === 'https:',
      respect_dnt: true,

      loaded: () => {
        if (process.env.NODE_ENV === 'development') {
          console.log('[Analytics] PostHog ready');
        }
      },
    });

    initialized = true;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[Analytics] PostHog initialization failed', error);
    }
  }
}

export { posthog };
